// Athlete Service - Handles athlete registration with service account
// This service uses server-side operations or service role for secure database writes

import { supabase } from '../lib/supabase'
import { createClient } from '@supabase/supabase-js'

// For development, we'll use a serverless function approach
// In production, this should be handled by a proper backend API

class AthleteService {
  // Create athlete using edge function with service role
  async createAthlete(athleteData) {
    try {
      // Debug: Log what data is being sent
      console.log('Sending to edge function:', {
        athleteData: athleteData,
        mediaFiles: [],
        socialMedia: athleteData.socialMedia || {},
        achievements: athleteData.achievements || []
      });

      const { data, error } = await supabase.functions.invoke('create-athlete', {
        body: {
          athleteData: athleteData,
          mediaFiles: [],
          socialMedia: athleteData.socialMedia || {},
          achievements: athleteData.achievements || []
        }
      })

      if (error) {
        console.error('Function error:', error)
        throw error
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to create athlete')
      }

      return data.athlete
    } catch (error) {
      console.error('Service function error:', error)
      throw error
    }
  }

  // Upload file to storage and save record
  async uploadFile(file, athleteId, fileType, title) {
    if (!file) return null

    try {
      // Create service role client for storage uploads
      const serviceRoleClient = createClient(
        supabase.supabaseUrl,
        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5peWl6eWt2bWlyY3V1enptdGFmIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc3NjI3NTg1MCwiZXhwIjoyMDkxODUxODUwfQ.2s1Esbx65iUJP3kxS027YJ_SO6ABph_8SRUn1vIVlNg'
      )

      const fileName = `${athleteId}/${Date.now()}-${file.name}`
      const { data, error } = await serviceRoleClient.storage
        .from('athlete-media')
        .upload(fileName, file)

      if (error) throw error

      // Save file record to database
      const { data: mediaRecord, error: mediaError } = await serviceRoleClient
        .from('athlete_media')
        .insert({
          athlete_id: athleteId,
          file_type: fileType,
          file_name: file.name,
          file_path: data.path,
          file_size: file.size,
          mime_type: file.type,
          description: title
        })
        .select()
        .single()

      if (mediaError) throw mediaError
      return mediaRecord
    } catch (error) {
      console.error('File upload error:', error)
      throw error
    }
  }

  // Save social media links
  async saveSocialMedia(athleteId, socialMedia) {
    if (!socialMedia || Object.keys(socialMedia).length === 0) return

    try {
      console.log('Saving social media:', { athleteId, socialMedia })
      
      // Use service role client to bypass RLS
      const serviceRoleClient = createClient(
        supabase.supabaseUrl,
        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5peWl6eWt2bWlyY3V1enptdGFmIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc3NjI3NTg1MCwiZXhwIjoyMDkxODUxODUwfQ.2s1Esbx65iUJP3kxS027YJ_SO6ABph_8SRUn1vIVlNg'
      )
      
      const socialMediaEntries = Object.entries(socialMedia)
        .filter(([_, url]) => url && url.trim() !== '')
        .map(([platform, url]) => ({
          athlete_id: athleteId,
          platform: platform.toLowerCase(),
          url: url,
          username: url.split('/').pop() || ''
        }))

      console.log('Social media entries to insert:', socialMediaEntries)

      if (socialMediaEntries.length > 0) {
        const { data, error } = await serviceRoleClient
          .from('athlete_social_media')
          .upsert(socialMediaEntries, {
            onConflict: 'athlete_id,platform'
          })

        console.log('Social media upsert response:', { data, error })

        if (error) throw error
      }
    } catch (error) {
      console.error('Social media save error:', error)
      throw error
    }
  }

  // Save achievements
  async saveAchievements(athleteId, achievements) {
    if (!achievements || achievements.length === 0) return

    try {
      // Use service role client to bypass RLS
      const serviceRoleClient = createClient(
        supabase.supabaseUrl,
        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5peWl6eWt2bWlyY3V1enptdGFmIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc3NjI3NTg1MCwiZXhwIjoyMDkxODUxODUwfQ.2s1Esbx65iUJP3kxS027YJ_SO6ABph_8SRUn1vIVlNg'
      )

      const achievementEntries = achievements.map(achievement => ({
        athlete_id: athleteId,
        achievement_type: achievement.type || 'recognition',
        title: achievement.title,
        description: achievement.description,
        date_achieved: achievement.date,
        level: achievement.level || 'college',
        organization: achievement.organization
      }))

      const { error } = await serviceRoleClient
        .from('athlete_achievements')
        .insert(achievementEntries)

      if (error) throw error
    } catch (error) {
      console.error('Achievements save error:', error)
      throw error
    }
  }

  // Create auth user
  async createAuthUser(email, password, athleteId, athleteData) {
    try {
      console.log('Creating auth user:', { email, athleteId, athleteData })
      
      // Use service role client to create user without email verification
      const serviceRoleClient = createClient(
        supabase.supabaseUrl,
        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5peWl6eWt2bWlyY3V1enptdGFmIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc3NjI3NTg1MCwiZXhwIjoyMDkxODUxODUwfQ.2s1Esbx65iUJP3kxS027YJ_SO6ABph_8SRUn1vIVlNg'
      )

      // Create full name for display
      const fullName = `${athleteData.firstName || ''} ${athleteData.middleName || ''} ${athleteData.lastName || ''}`.trim()

      const { data, error } = await serviceRoleClient.auth.admin.createUser({
        email: email,
        password: password,
        email_confirm: true,
        phone: athleteData.phone || null,
        user_metadata: {
          athlete_id: athleteId,
          full_name: fullName,
          first_name: athleteData.firstName,
          last_name: athleteData.lastName,
          phone: athleteData.phone
        },
        app_metadata: {
          provider: 'email',
          role: 'athlete'
        }
      })

      console.log('Auth creation response:', { data, error })

      if (error) {
        console.warn('❌ Auth user creation failed:', error)
        // Continue even if auth fails - athlete record is created
      } else {
        console.log('✅ Auth user created successfully:', data.user?.id)
        console.log('✅ User profile data:', {
          email: data.user?.email,
          phone: data.user?.phone,
          metadata: data.user?.user_metadata
        })
      }
    } catch (error) {
      console.error('❌ Auth creation error:', error)
      // Don't throw - allow registration to continue
    }
  }
}

export default new AthleteService()
